/**
 * @file goods-filter.js
 * @description 商品一覧の動的描画・カテゴリー複数選択フィルター
 */

import axios from "axios";
import { gsap } from "gsap";
import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";

const ALL_CATEGORY = "all";
const DEFAULT_IMAGE = "img-goods01.jpg";
const PENDING_IMAGE = "未着";
const STORE_ONLY_URL = "店舗のみ";

/**
 * @typedef GoodsItem
 * @property {number} id
 * @property {string} name
 * @property {string} cat
 * @property {string} cat_name
 * @property {number} price
 * @property {string} [img]
 * @property {string} url
 */

/**
 * @typedef CategoryItem
 * @property {string} cat
 * @property {string} cat_name
 * @property {number} count
 */

/**
 * goods.json を取得
 * @param {string} url
 * @returns {Promise<GoodsItem[]>}
 */
async function loadGoods(url) {
  try {
    const res = await axios.get(url, { params: { t: String(Date.now()) } });
    const data = res?.data;
    if (!Array.isArray(data)) {
      console.warn("[goods] JSONが配列ではありません:", data);
      return [];
    }
    return data.filter(
      (item) =>
        item &&
        typeof item.id === "number" &&
        typeof item.name === "string" &&
        typeof item.cat === "string" &&
        typeof item.cat_name === "string" &&
        typeof item.price === "number" &&
        typeof item.url === "string",
    );
  } catch (err) {
    console.error("[goods] 取得に失敗:", err);
    return [];
  }
}

/**
 * カテゴリー一覧を件数降順で生成（ALLは先頭）
 * @param {GoodsItem[]} goods
 * @returns {CategoryItem[]}
 */
function buildCategories(goods) {
  const map = new Map();

  for (const item of goods) {
    const key = item.cat_name;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(key, { cat: key, cat_name: item.cat_name, count: 1 });
    }
  }

  const sorted = [...map.values()].sort((a, b) => b.count - a.count);

  return [{ cat: ALL_CATEGORY, cat_name: "ALL", count: goods.length }, ...sorted];
}

/**
 * 価格を表示用にフォーマット
 * @param {number} price
 * @returns {string}
 */
function formatPrice(price) {
  return `￥${price.toLocaleString("ja-JP")}`;
}

/**
 * HTMLエスケープ
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * 商品名を表示用にフォーマット（改行を <br> にする）
 * @param {string} name
 * @returns {string}
 */
function formatGoodsName(name) {
  return escapeHtml(name).replace(/\r\n|\r|\n/g, "<br>");
}

/**
 * 商品画像が用意されているか（未着・空はプレースホルダー）
 * @param {string} [img]
 * @returns {boolean}
 */
function hasGoodsImage(img) {
  const filename = String(img ?? "").trim();
  return filename !== "" && filename !== PENDING_IMAGE;
}

/**
 * 外部リンクとして有効なURLか（店舗のみ・空はリンクなし）
 * @param {string} [url]
 * @returns {boolean}
 */
function hasGoodsLink(url) {
  const href = String(url ?? "").trim();
  return href !== "" && href !== STORE_ONLY_URL;
}

/**
 * 商品画像のパスを解決（本番ビルド時は webp に変換）
 * @param {string} imgBase
 * @param {string} [img]
 * @returns {string}
 */
function getGoodsImagePath(imgBase, img) {
  const filename = String(img || DEFAULT_IMAGE);
  const resolvedFilename = import.meta.env.PROD
    ? filename.replace(/\.(jpe?g|png)$/i, ".webp")
    : filename;
  return `${imgBase}/${resolvedFilename}`;
}

export class GoodsFilter {
  /**
   * @param {object} options
   * @param {HTMLElement} options.sectionEl
   * @param {string} options.jsonUrl
   */
  constructor({ sectionEl, jsonUrl }) {
    this.sectionEl = sectionEl;
    this.jsonUrl = jsonUrl;
    this.categoryListEl = sectionEl.querySelector(".category-list__items");
    this.categoryListWrapEl = sectionEl.querySelector(".category-list");
    this.toggleBtn = sectionEl.querySelector(".category-list__toggle");
    this.goodsListScrollEl = sectionEl.querySelector(".goods-list-scroll");
    this.goodsListEl = sectionEl.querySelector(".goods-list");
    this.simpleBar = null;
    this.imgBase = sectionEl.dataset.imgBase || "./assets/img/goods";
    this.goods = [];
    this.selectedCategories = new Set([ALL_CATEGORY]);
    this.isInitialRender = true;
    this.filterTimeline = null;
    this.spinnerEl = null;
    this.isCategoryExpanded = false;
    this.categoryRows = [];
    this.categoryAccordionTimeline = null;
    this.isCategoryAccordionAnimating = false;
    this.resizeTimer = null;
  }

  async init() {
    if (!this.categoryListEl || !this.goodsListEl) {
      console.warn("[goods] 必要なDOM要素が見つかりません");
      return;
    }

    this.goods = await loadGoods(this.jsonUrl);
    if (this.goods.length === 0) return;

    this.renderCategories();
    this.renderGoods();
    this.initSpinner();
    this.initSimpleBar();
    this.bindEvents();

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    this.syncCategoryAccordion();
    this.isInitialRender = false;
  }

  // ── スピナー ────────────────────────────────────────────

  initSpinner() {
    const parent = this.goodsListScrollEl?.parentElement ?? this.sectionEl;
    if (getComputedStyle(parent).position === "static") {
      parent.style.position = "relative";
    }

    const el = document.createElement("div");
    el.className = "goods-spinner";
    el.setAttribute("aria-hidden", "true");
    Object.assign(el.style, {
      position: "absolute",
      top: "100px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
      zIndex: "10",
    });

    const ring = document.createElement("div");
    Object.assign(ring.style, {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      border: "4px solid #F08F71",
      borderTopColor: "transparent",
      opacity: "0.8",
    });

    el.appendChild(ring);
    parent.appendChild(el);

    gsap.set(el, { opacity: 0 });
    gsap.to(ring, { rotation: 360, duration: 0.8, ease: "none", repeat: -1 });

    this.spinnerEl = el;
  }

  showSpinner() {
    if (!this.spinnerEl) return;
    gsap.to(this.spinnerEl, { opacity: 1, duration: 0.15, overwrite: true });
  }

  hideSpinner() {
    if (!this.spinnerEl) return;
    gsap.to(this.spinnerEl, { opacity: 0, duration: 0.2, overwrite: true });
  }

  // ── スクロール ──────────────────────────────────────────

  initSimpleBar() {
    if (!this.goodsListScrollEl || this.simpleBar) return;

    this.simpleBar = new SimpleBar(this.goodsListScrollEl, {
      autoHide: false,
    });
  }

  recalculateScroll() {
    requestAnimationFrame(() => {
      this.simpleBar?.recalculate();
    });
  }

  // ── 描画 ────────────────────────────────────────────────

  renderCategories() {
    const categories = buildCategories(this.goods);
    const fragment = document.createDocumentFragment();

    for (const category of categories) {
      const li = document.createElement("li");
      const isActive = this.selectedCategories.has(category.cat);

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `category-list__btn${isActive ? " is-active" : ""}`;
      btn.dataset.cat = category.cat;
      btn.setAttribute("aria-pressed", String(isActive));
      btn.textContent = category.cat_name;

      li.appendChild(btn);
      fragment.appendChild(li);
    }

    this.categoryListEl.replaceChildren(fragment);
    this.isCategoryExpanded = false;
  }

  renderGoods() {
    const fragment = document.createDocumentFragment();

    for (const item of this.goods) {
      const li = document.createElement("li");
      li.dataset.cat = item.cat_name;
      const namePlain = escapeHtml(item.name).replace(/\r\n|\r|\n/g, " ");
      const media = hasGoodsImage(item.img)
        ? `<img src="${escapeHtml(getGoodsImagePath(this.imgBase, item.img))}" alt="${namePlain}" width="280" height="280" loading="lazy" decoding="async" itemprop="image">`
        : `<div class="goods-item__placeholder" role="img" aria-label="${namePlain}"></div>`;
      const innerClass = `goods-item__inner${item.sold_out ? " sold-out" : ""}`;
      const content = `
            ${media}
            <div class="goods-item__content">
            <h3 class="name" itemprop="name">${formatGoodsName(item.name)}</h3>
            <p class="price" itemprop="description">${formatPrice(item.price)}</p>
            </div>`;
      const inner = hasGoodsLink(item.url)
        ? `<a href="${escapeHtml(item.url)}" class="${innerClass}" target="_blank" rel="noopener noreferrer" itemprop="url">${content}
          </a>`
        : `<div class="${innerClass}">${content}
          </div>`;
      li.innerHTML = `
        <article class="goods-item" itemscope itemtype="https://schema.org/Product">
          ${inner}
        </article>
      `;
      fragment.appendChild(li);
    }

    this.goodsListEl.replaceChildren(fragment);
    this.applyFilter(false);
    this.recalculateScroll();
  }

  // ── フィルター ──────────────────────────────────────────

  /**
   * 商品が選択カテゴリーに一致するか
   * @param {string} cat
   * @returns {boolean}
   */
  isItemVisible(cat) {
    if (this.selectedCategories.has(ALL_CATEGORY) || this.selectedCategories.size === 0) {
      return true;
    }
    return this.selectedCategories.has(cat);
  }

  /**
   * 進行中のアニメーションをすべて停止しスタイルをリセット
   */
  stopFilterAnimation() {
    if (this.filterTimeline) {
      this.filterTimeline.kill();
      this.filterTimeline = null;
    }
    this.hideSpinner();
  }

  /**
   * 表示対象アイテムを Fisher-Yates シャッフルで DOM 上で並び替え
   * @param {HTMLElement[]} items
   */
  shuffleVisibleItems(items) {
    const visible = items.filter((item) => this.isItemVisible(item.dataset.cat));
    for (let i = visible.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [visible[i], visible[j]] = [visible[j], visible[i]];
    }
    for (const item of visible) {
      this.goodsListEl.appendChild(item);
    }
  }

  /**
   * 商品の表示状態を設定（アニメーションなし）
   * @param {HTMLElement} item
   * @param {boolean} visible
   */
  setItemVisibility(item, visible) {
    if (visible) {
      item.classList.remove("is-hidden");
      item.style.display = "";
      item.removeAttribute("aria-hidden");
      gsap.set(item, { opacity: 1 });
      return;
    }
    item.classList.add("is-hidden");
    item.style.display = "none";
    item.setAttribute("aria-hidden", "true");
    gsap.set(item, { opacity: 0 });
  }

  /**
   * フィルターを適用
   * フェードアウト → DOM 整理（+ シャッフル）→ フェードイン の順で演出
   * @param {boolean} animate
   */
  applyFilter(animate = true) {
    const items = [...this.goodsListEl.querySelectorAll(":scope > li")];

    if (!animate || this.isInitialRender) {
      this.stopFilterAnimation();
      for (const item of items) {
        this.setItemVisibility(item, this.isItemVisible(item.dataset.cat));
      }
      this.recalculateScroll();
      return;
    }

    this.stopFilterAnimation();

    const isMultiCategory =
      !this.selectedCategories.has(ALL_CATEGORY) && this.selectedCategories.size > 1;

    // アニメーション前に表示/非表示グループを確定（selectedCategories は更新済み）
    const currentlyVisible = items.filter((item) => item.style.display !== "none");
    const willBeHidden = items.filter((item) => !this.isItemVisible(item.dataset.cat));
    const willBeVisible = items.filter((item) => this.isItemVisible(item.dataset.cat));

    this.showSpinner();

    const tl = gsap.timeline({
      onComplete: () => {
        this.filterTimeline = null;
        this.hideSpinner();
        this.recalculateScroll();
      },
    });
    this.filterTimeline = tl;

    // ① 現在表示中のアイテムをすべてフェードアウト
    if (currentlyVisible.length > 0) {
      tl.to(currentlyVisible, {
        opacity: 0,
        duration: 0.4,
        filter: "blur(6px)",
        ease: "power2.in",
      });
    }

    // ② フェードアウト完了後に DOM を整理
    tl.call(() => {
      // 複数カテゴリー選択時は表示対象をシャッフル
      if (isMultiCategory) {
        const freshItems = [...this.goodsListEl.querySelectorAll(":scope > li")];
        this.shuffleVisibleItems(freshItems);
      }

      // 非表示アイテムを隠す
      for (const item of willBeHidden) {
        item.classList.add("is-hidden");
        item.style.display = "none";
        item.setAttribute("aria-hidden", "true");
      }

      // 表示アイテムを opacity:0 で DOM 上に出現させる
      for (const item of willBeVisible) {
        item.classList.remove("is-hidden");
        item.style.display = "";
        item.removeAttribute("aria-hidden");
        gsap.set(item, { opacity: 0 });
      }
    });

    // ③ 表示アイテムをスタッガーでフェードイン
    tl.to(willBeVisible, {
      opacity: 1,
      duration: 0.4,
      filter: "blur(0px)",
      ease: "power2.out",
      stagger: 0.04,
    });
  }

  // ── カテゴリー折りたたみ ────────────────────────────────

  /**
   * ボタンを行ごとにグループ化
   * @param {HTMLElement[]} buttons
   * @returns {HTMLElement[][]}
   */
  groupButtonsByRow(buttons) {
    const rows = [];
    let currentRow = [];
    let currentTop = null;

    for (const btn of buttons) {
      const top = btn.getBoundingClientRect().top;

      if (currentTop === null || Math.abs(top - currentTop) <= 4) {
        currentRow.push(btn);
        if (currentTop === null) currentTop = top;
      } else {
        rows.push(currentRow);
        currentRow = [btn];
        currentTop = top;
      }
    }

    if (currentRow.length > 0) {
      rows.push(currentRow);
    }

    return rows;
  }

  /**
   * 3行目以降のカテゴリー li を取得
   * @returns {HTMLElement[]}
   */
  getAccordionHiddenItems() {
    const rows = this.categoryRows;
    if (!rows || rows.length <= 2) return [];

    const items = [];

    rows.forEach((rowButtons, rowIndex) => {
      if (rowIndex < 2) return;

      for (const btn of rowButtons) {
        const li = btn.closest("li");
        if (li) items.push(li);
      }
    });

    return items;
  }

  stopCategoryAccordionAnimation() {
    if (this.categoryAccordionTimeline) {
      this.categoryAccordionTimeline.kill();
      this.categoryAccordionTimeline = null;
    }
    this.isCategoryAccordionAnimating = false;
    if (this.toggleBtn) {
      this.toggleBtn.disabled = false;
    }
  }

  updateCategoryAccordionClasses() {
    if (!this.categoryListWrapEl || !this.toggleBtn) return;

    this.categoryListWrapEl.classList.toggle("is-expanded", this.isCategoryExpanded);
    this.categoryListWrapEl.classList.toggle("is-collapsed", !this.isCategoryExpanded);
    this.toggleBtn.setAttribute("aria-expanded", String(this.isCategoryExpanded));
  }

  /**
   * 3行目以降の表示/非表示を即時反映（アニメーションなし）
   */
  applyCategoryAccordionState() {
    if (!this.categoryListWrapEl || !this.toggleBtn || !this.categoryListEl) return;

    this.stopCategoryAccordionAnimation();

    const items = [...this.categoryListEl.querySelectorAll(":scope > li")];
    const rows = this.categoryRows;
    const hiddenItems = this.getAccordionHiddenItems();

    if (!rows || rows.length <= 2) {
      this.toggleBtn.hidden = true;
      this.isCategoryExpanded = false;
      this.categoryListWrapEl.classList.remove("is-collapsed", "is-expanded");
      this.toggleBtn.setAttribute("aria-expanded", "false");

      for (const li of items) {
        li.classList.remove("is-accordion-hidden");
        gsap.set(li, { clearProps: "opacity,transform" });
      }
      return;
    }

    this.toggleBtn.hidden = false;
    this.updateCategoryAccordionClasses();

    for (const li of items) {
      li.classList.remove("is-accordion-hidden");
      gsap.set(li, { clearProps: "opacity,transform" });
    }

    if (!this.isCategoryExpanded) {
      for (const li of hiddenItems) {
        li.classList.add("is-accordion-hidden");
      }
    }
  }

  /**
   * カテゴリーアコーディオンの開閉アニメーション
   */
  animateCategoryAccordion(expand) {
    const hiddenItems = this.getAccordionHiddenItems();
    if (hiddenItems.length === 0) {
      this.isCategoryExpanded = expand;
      this.applyCategoryAccordionState();
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      this.isCategoryExpanded = expand;
      this.applyCategoryAccordionState();
      return;
    }

    this.stopCategoryAccordionAnimation();
    this.isCategoryAccordionAnimating = true;
    this.isCategoryExpanded = expand;
    this.updateCategoryAccordionClasses();
    this.toggleBtn.disabled = true;

    if (expand) {
      for (const li of hiddenItems) {
        li.classList.remove("is-accordion-hidden");
      }

      gsap.set(hiddenItems, { opacity: 0, y: 12 });

      this.categoryAccordionTimeline = gsap.to(hiddenItems, {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: "power2.out",
        stagger: 0.05,
        onComplete: () => {
          gsap.set(hiddenItems, { clearProps: "opacity,transform" });
          this.stopCategoryAccordionAnimation();
        },
      });
      return;
    }

    this.categoryAccordionTimeline = gsap.to(hiddenItems, {
      opacity: 0,
      y: -8,
      duration: 0.3,
      ease: "power2.in",
      stagger: 0.04,
      onComplete: () => {
        for (const li of hiddenItems) {
          li.classList.add("is-accordion-hidden");
        }
        gsap.set(hiddenItems, { clearProps: "opacity,transform" });
        this.stopCategoryAccordionAnimation();
      },
    });
  }

  /**
   * 3行以上のとき2行目まで表示し、3行目以降をアコーディオンで隠す
   */
  syncCategoryAccordion() {
    if (!this.categoryListWrapEl || !this.toggleBtn || !this.categoryListEl) return;

    const items = [...this.categoryListEl.querySelectorAll(":scope > li")];

    for (const li of items) {
      li.classList.remove("is-accordion-hidden");
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const buttons = items
          .map((li) => li.querySelector(".category-list__btn"))
          .filter(Boolean);

        this.categoryRows = buttons.length > 0 ? this.groupButtonsByRow(buttons) : [];
        this.applyCategoryAccordionState();
      });
    });
  }

  handleCategoryAccordionToggle() {
    if (this.isCategoryAccordionAnimating) return;
    this.animateCategoryAccordion(!this.isCategoryExpanded);
  }

  scheduleCategoryAccordionSync() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.syncCategoryAccordion();
    }, 150);
  }

  // ── カテゴリー操作 ──────────────────────────────────────

  updateCategoryButtons() {
    const buttons = this.categoryListEl.querySelectorAll(".category-list__btn");
    for (const btn of buttons) {
      const isActive = this.selectedCategories.has(btn.dataset.cat);
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", String(isActive));
    }
  }

  handleCategoryClick(cat) {
    if (cat === ALL_CATEGORY) {
      this.selectedCategories = new Set([ALL_CATEGORY]);
    } else {
      this.selectedCategories.delete(ALL_CATEGORY);

      if (this.selectedCategories.has(cat)) {
        this.selectedCategories.delete(cat);
      } else {
        this.selectedCategories.add(cat);
      }

      if (this.selectedCategories.size === 0) {
        this.selectedCategories = new Set([ALL_CATEGORY]);
      }
    }

    this.updateCategoryButtons();
    this.applyFilter(true);
  }

  bindEvents() {
    this.categoryListEl.addEventListener("click", (e) => {
      const btn = e.target.closest(".category-list__btn");
      if (!btn) return;
      this.handleCategoryClick(btn.dataset.cat);
    });

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleCategoryAccordionToggle();
      });
    }

    window.addEventListener("resize", () => this.scheduleCategoryAccordionSync());
  }
}

/**
 * 商品フィルターを初期化
 * @param {object} options
 * @param {HTMLElement} options.sectionEl
 * @param {string} [options.jsonUrl]
 */
export async function initGoodsFilter({ sectionEl, jsonUrl = "./data/goods.json" }) {
  if (!sectionEl) return;

  try {
    const filter = new GoodsFilter({ sectionEl, jsonUrl });
    await filter.init();
  } catch (err) {
    console.error("[goods] 初期化に失敗:", err);
  }
}
